import React, { useState } from "react";
import {
  Drawer,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

function AddNewUserToggle() {
  const [open, setOpen] = useState(false);
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    username: "",
    role: "",
  });

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Responsive handling

  const roleOptions = [
    { label: "Super Admin", value: "super-admin" },
    { label: "Data Admin", value: "data-admin" },
    { label: "Website Admin", value: "website-admin" },
  ];

  const toggleDrawer = (isOpen) => () => setOpen(isOpen);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!loginData.email || !loginData.password || !loginData.confirmPassword || !loginData.role || !loginData.username) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (loginData.password !== loginData.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      const response = await axios.post("/api/register", loginData);

      if (response.status === 200 || response.status === 201) {
        toast.success(`Successfully Registered: ${loginData.email}`);
        setOpen(false);
        setLoginData({ email: "", password: "", confirmPassword: "", username: "", role: "" });
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Error during registration:", error);
      toast.error(error.response?.data?.message || "An error occurred. Please try again.");
    }
  };

  return (
    <Box sx={{ textAlign: "right", mt: 1, fullWidth: "lg" }}>
      {/* Enhanced Button with subtle shadow and transition */}
      <Button
        variant="contained"
        fullWidth={isSmallScreen}
        onClick={toggleDrawer(true)}
        sx={{
          background: "linear-gradient(135deg, #4A90E2, #8ECAE6)",
          color: "#fff",
          fontWeight: "bold",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s ease",
          ":hover": { 
            background: "linear-gradient(135deg, #357ABD, #66B3E6)",
            boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
            transform: "translateY(-1px)"
          },
        }}
      >
        Add New User
      </Button>

      {/* Enhanced Drawer with better visual hierarchy */}
      <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
        <Paper 
          elevation={3}
          sx={{
            height: "100%",
            p: 3,
            width: isSmallScreen ? "100vw" : "400px",
            background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
            display: "flex",
            flexDirection: "column",
          }}
          role="presentation"
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{ 
                color: "#1976D2", 
                textAlign: "center",
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                mb: 1
              }}
            >
              Create Account
            </Typography>
            <Divider sx={{ 
              borderColor: "#1976D2", 
              borderWidth: 1, 
              width: "80px", 
              mx: "auto",
              mb: 3 
            }} />
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack spacing={3} sx={{ flexGrow: 1 }}>
              <TextField
                label="Email"
                name="email"
                type="email"
                fullWidth
                value={loginData.email}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{ 
                  bgcolor: "white", 
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E0E0E0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#1976D2",
                    },
                  },
                }}
              />
              
              <TextField
                label="Password"
                name="password"
                type="password"
                fullWidth
                value={loginData.password}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{ 
                  bgcolor: "white", 
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E0E0E0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#1976D2",
                    },
                  },
                }}
              />
              
              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                fullWidth
                value={loginData.confirmPassword}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{ 
                  bgcolor: "white", 
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E0E0E0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#1976D2",
                    },
                  },
                }}
              />
              
              <TextField
                label="Username"
                name="username"
                fullWidth
                value={loginData.username}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{ 
                  bgcolor: "white", 
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E0E0E0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#1976D2",
                    },
                  },
                }}
              />

              {/* Enhanced Role Selection */}
              <FormControl fullWidth required sx={{ 
                bgcolor: "white",
                borderRadius: 1,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#1976D2",
                  },
                },
              }}>
                <InputLabel id="role-label" sx={{ color: "text.secondary" }}>Role</InputLabel>
                <Select 
                  labelId="role-label" 
                  name="role" 
                  value={loginData.role} 
                  onChange={handleChange}
                  label="Role"
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Enhanced Submit Button with animation */}
              <Box sx={{ pt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    background: "linear-gradient(135deg, #4CAF50, #81C784)",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    py: 1.5,
                    boxShadow: "0 4px 6px rgba(76, 175, 80, 0.2)",
                    transition: "all 0.3s ease",
                    ":hover": { 
                      background: "linear-gradient(135deg, #388E3C, #66BB6A)",
                      boxShadow: "0 6px 8px rgba(76, 175, 80, 0.3)",
                      transform: "translateY(-1px)"
                    },
                  }}
                >
                  Create Account
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Drawer>
    </Box>
  );
}

export default AddNewUserToggle;